<template>
  <AppCard class="border" elevation="0">
    <div class="d-flex align-center mb-5">
      <!-- Title -->
      <p class="mb-0 text--medium text--bold">
        {{ $t('subscriptions.views.SubscriptionConsumption.DataroomStorage.title') }}
        <AppInfoTooltip v-if="subscriptionDataroomStorage">
          <p class="font-weight-semi-bold">{{ $t('subscriptions.views.SubscriptionConsumption.DataroomStorage.details') }}</p>
          <p>
            {{ $t('subscriptions.views.SubscriptionConsumption.DataroomStorage.subscriptionDate') }}
            {{ ISOToDDMMYYYY(subscriptionDataroomStorage.startDate) }}
          </p>
          <p>
            {{ subscriptionDataroomStorage.autoRenew ? $t('subscriptions.views.SubscriptionConsumption.DataroomStorage.renewalDate') : $t('subscriptions.views.SubscriptionConsumption.DataroomStorage.endDate') }}
            {{ ISOToDDMMYYYY(subscriptionDataroomStorage.endDate) }}
          </p>
        </AppInfoTooltip>
      </p>

      <!-- Share Button -->
      <app-button class="ml-auto"
                  type="icon"
                  @click="openShareSubscriptionConsumptionDialog()"
      >
        <app-material-icon name="share" type="fa" size="sm" weight="fal" />
      </app-button>
      <!-- Manage Storage -->
      <app-button
                  type="icon"
                  :disabled="subscriptionDataroomStoragePending || subscriptionDataroomStorageError"
                  @click="openDataroomStorageManagementDialog"
      >
        <app-material-icon name="gear" type="fa" size="sm" weight="fal" />
      </app-button>
    </div>

    <template v-if="subscriptionDataroomStorageError">
      <app-alert-in-page :dismissible="false"
                         variant="error"
                         no-icon
      >
        {{ $t('subscriptions.views.SubscriptionConsumption.DataroomStorage.error') }}
      </app-alert-in-page>
    </template>
    <template v-else>
      <div v-if="subscriptionDataroomStoragePending" class="text-center">
        <v-skeleton-loader type="text"/>
      </div>
      <template v-else-if="subscriptionDataroomStorage">
        <div class="my-6">
          <div class="d-flex align-center column-gap-1 mb-1">
            <app-material-icon
              v-if="isSubscriptionDataroomStorageExceeded"
              weight="fas"
              name="diamond-exclamation"
              type="fa"
              size="xs"
              class="text-error mr-1"
            />
            <div class="text-grey-700" :class="{ 'text-error': isSubscriptionDataroomStorageExceeded }">
              {{ subscriptionDataroomStorage.currentStorage }} Go
              <i18n-t keypath="subscriptions.views.SubscriptionConsumption.DataroomStorage.used"
                      tag="span"
                      :plural="subscriptionDataroomStorage.currentStorage"
              />
              {{ subscriptionDataroomStorage.limitGb }} Go
            </div>
          </div>

          <!-- Data visualisation -->
          <v-progress-linear height="20" rounded>
            <template v-for="(dr, index) in subscriptionDatarooms" :key="index">
              <div class="h-100"
                   :style="{ width: dr.percent + '%', backgroundColor: DATA_VISUALISATION_COLORS[dr.color] }"
              >
              </div>
            </template>
          </v-progress-linear>
        </div>

        <!-- Datarooms -->
        <p class="text--small text--bold">
          {{ $t('subscriptions.views.SubscriptionConsumption.DataroomStorage.storageDistribution') }}
        </p>
        <div v-for="(dr, index) in displayedSubscriptionDatarooms" :key="index" class="mt-2">
          <div class="d-flex align-center">
            <div class="mr-2"
                 :style="{ backgroundColor: DATA_VISUALISATION_COLORS[dr.color] }"
                 style="width: 12px; height: 12px; border-radius: 4px"
            />
            <span class="text-truncate" style="max-width: 70%;">{{ dr.name }}</span>
            <span class="ml-auto">{{ dr.storage }} Go</span>
          </div>
        </div>

        <!-- Display all -->
        <app-button v-if="subscriptionDatarooms.length > MAX_DATAROOMS_TO_DISPLAY"
                    class="mt-4"
                    size="small"
                    type="outlined"
                    variant="neutral"
                    @click="displayAll = !displayAll"
        >
          {{ $t(`subscriptions.views.SubscriptionConsumption.DataroomStorage.${displayAll ? 'hide' : 'displayAll'}`) }}
          <app-material-icon :name="`chevron-${displayAll ? 'up' : 'down'}`" type="fa" size="sm" />
        </app-button>
      </template>
    </template>

    <!-- DIALOGS -->
    <SubscriptionDataroomStorageManagementDialog v-if="dataroomStorageManagementDialogIsOpen"
                                                 @close="closeDataroomStorageManagementDialog"
    />
    <ShareSubscriptionConsumptionDialog v-if="shareSubscriptionConsumptionDialogIsOpen"
                                        :title="shareSubscriptionConsumptionDialogTitle"
                                        :submit-action="SHARE_SUBSCRIPTION_DATAROOM_PLAN"
                                        :ok-loading="shareSubscriptionDataroomPlanPending"
                                        @close="closeShareSubscriptionConsumptionDialog"
    />
  </AppCard>
</template>

<script>
  import { mapState, mapActions } from 'vuex'

  import AppCard from '@/common/AppCard.vue'
  import AppInfoTooltip from '@/common/AppInfoTooltip.vue'
  import { DATA_VISUALISATION_COLORS } from '@/common/utils/colors'
  import { ISOToDDMMYYYY } from '@/common/utils/dates'
  import { toast } from '@/common/utils/toast'
  import {
    GET_SUBSCRIPTION_DATAROOM_STORAGE,
    SHARE_SUBSCRIPTION_DATAROOM_PLAN,
  } from '@/store/modules/subscription/action_types'
  import ShareSubscriptionConsumptionDialog from "@/subscriptions/views/ShareSubscriptionConsumptionDialog.vue";

  import SubscriptionDataroomStorageManagementDialog from './SubscriptionDataroomStorageManagementDialog.vue'

  const MAX_DATAROOMS_TO_DISPLAY = 3

  export default {
    name: 'SubscriptionConsumptionDataroomStorage',
    components: {ShareSubscriptionConsumptionDialog, AppCard, AppInfoTooltip, SubscriptionDataroomStorageManagementDialog },
    data () {
      return {
        ISOToDDMMYYYY,
        DATA_VISUALISATION_COLORS,
        MAX_DATAROOMS_TO_DISPLAY,
        displayAll: false,
        dataroomStorageManagementDialogIsOpen: false,
        shareSubscriptionConsumptionDialogIsOpen: false,
      }
    },
    computed: {
      ...mapState('subscription', [
        'subscriptionDataroomStoragePending',
        'subscriptionDataroomStorage',
        'subscriptionDataroomStorageError',
        'shareSubscriptionDataroomPlanPending',
      ]),
      isSubscriptionDataroomStorageExceeded () {
        return this.subscriptionDataroomStorage?.currentStorage > parseFloat(this.subscriptionDataroomStorage.limitGb)
      },
      subscriptionDatarooms () {
        const { datarooms } = this.subscriptionDataroomStorage
        const colors = Object.keys(DATA_VISUALISATION_COLORS)
        datarooms.forEach((dataroom, index) => {
          const colorIndex = index % colors.length
          dataroom.color = colors[colorIndex]
          dataroom.percent = (dataroom.storage / parseInt(this.subscriptionDataroomStorage.limitGb)) * 100
        })
        return datarooms
      },
      displayedSubscriptionDatarooms () {
        if (this.displayAll) {
          return this.subscriptionDatarooms
        }
        return this.subscriptionDatarooms.slice(0, MAX_DATAROOMS_TO_DISPLAY)
      },
      shareSubscriptionConsumptionDialogTitle () {
        return this.$t('subscriptions.views.SubscriptionConsumption.ShareDialog.title', {
          itemName: this.$t('subscriptions.views.SubscriptionConsumption.DataroomStorage.title'),
        })
      },
    },
    created () {
      this.getSubscriptionDataroomStorage()
    },
    methods: {
      ...mapActions('subscription', [GET_SUBSCRIPTION_DATAROOM_STORAGE, SHARE_SUBSCRIPTION_DATAROOM_PLAN]),
      async getSubscriptionDataroomStorage () {
        try {
          await this.GET_SUBSCRIPTION_DATAROOM_STORAGE()
        } catch (error) {
          toast.error(this.$t('subscriptions.views.SubscriptionConsumption.DataroomStorage.error'))
        }
      },
      openDataroomStorageManagementDialog () {
        this.dataroomStorageManagementDialogIsOpen = true
      },
      closeDataroomStorageManagementDialog () {
        this.dataroomStorageManagementDialogIsOpen = false
      },
      openShareSubscriptionConsumptionDialog () {
        this.shareSubscriptionConsumptionDialogIsOpen = true
      },
      closeShareSubscriptionConsumptionDialog () {
        this.shareSubscriptionConsumptionDialogIsOpen = false
      },
    },
  }
</script>

<style scoped>
  :deep(.v-progress-linear__content) {
    display: flex;
    justify-content: flex-start;
    background-color: rgb(var(--v-theme-grey-50));
  }
</style>
